import type { RequestContext } from "../context/request-context.js";
import { PermissionCode } from "../../permissions/permissions.js";
import type { NdtReportCommon, NdtDocumentCommon, NdtReportStatus } from "../../domain/ndt/ndt-report-common.js";
import type { MeReportService } from "./me-report-service.js";
import type { LpReportService } from "./lp-report-service.js";
import type { PmReportService } from "./pm-report-service.js";
import type { ConventionalUsReportService } from "./conventional-us-report-service.js";
import type { BscanReportService } from "./bscan-report-service.js";

/**
 * CENTRAL DE RELATÓRIOS END (Bloco 05) — consolidador só de leitura.
 *
 * Reaproveita os QUATRO serviços oficiais de END que já existem e já têm
 * repositório/persistência próprios: ME (Medição de Espessura), LP (Líquido
 * Penetrante), PM (Partículas Magnéticas) e US-Convencional. Nunca cria,
 * edita nem duplica relatório — só localiza, filtra e organiza o que os
 * módulos oficiais já têm, no mesmo espírito de
 * `InspectionBookConsolidatorService`.
 *
 * B-SCAN NÃO é listado aqui: diferente de ME/LP/PM/USC, B-Scan não existe
 * neste sistema como um tipo de relatório independente com serviço e
 * repositório próprios — hoje ele é só uma fonte de leitura para o cálculo
 * de AVR (`BscanAvrSourceAdapter`) e um builder de PDF isolado
 * (`ndt-builders.ts`), sem entidade "Relatório B-Scan" listável, com número,
 * revisão e status. Listar um "relatório" que não existe como tal seria
 * inventar dado — por isso fica de fora, e isso é intencional, não uma
 * lacuna esquecida.
 */

export type NdtReportType = "ME" | "LP" | "PM" | "USC" | "BSCAN";

export const NDT_REPORT_TYPE_LABELS: Record<NdtReportType, string> = {
  ME: "Medição de Espessura",
  LP: "Líquido Penetrante",
  PM: "Partículas Magnéticas",
  USC: "Ultrassom Convencional",
  BSCAN: "Ultrassom B-Scan / C-Scan"
};

/** Rota da própria página do relatório (a mesma serve VISUALIZAR e BAIXAR — o Gerar PDF já vive lá). */
const NDT_REPORT_ROUTES: Record<NdtReportType, string> = {
  ME: "/ndt/thickness",
  LP: "/ndt/lp",
  PM: "/ndt/pm",
  USC: "/ndt/us-conventional",
  BSCAN: "/ndt/bscan"
};

const NDT_VIEW_PERMISSION: Record<NdtReportType, string> = {
  ME: PermissionCode.MeReportView,
  LP: PermissionCode.LpReportView,
  PM: PermissionCode.PmReportView,
  USC: PermissionCode.UscReportView,
  BSCAN: PermissionCode.BscanReportView
};

export interface NdtReportRow {
  readonly id: string;
  readonly type: NdtReportType;
  readonly typeLabel: string;
  readonly reportNumber: string;
  readonly revision: string;
  readonly date: string;
  readonly projectId: string | null;
  readonly projectLabel: string;
  readonly unitAreaId: string | null;
  readonly unitAreaLabel: string;
  readonly equipmentTag: string;
  readonly responsible: string;
  readonly status: NdtReportStatus;
  readonly viewRoute: string;
  readonly downloadRoute: string;
}

export interface NdtReportCatalogFilter {
  readonly from?: string;
  readonly to?: string;
  readonly projectId?: string;
  readonly unitAreaId?: string;
  readonly equipmentTag?: string;
  readonly type?: NdtReportType;
  readonly status?: NdtReportStatus;
}

interface Deps {
  meReportService: MeReportService;
  lpReportService: LpReportService;
  pmReportService: PmReportService;
  conventionalUsReportService: ConventionalUsReportService;
  /** Opcional: composições antigas do catálogo continuam válidas sem ele. */
  bscanReportService?: BscanReportService;
}

function toRow(type: NdtReportType, report: NdtReportCommon<NdtDocumentCommon>): NdtReportRow {
  const route = NDT_REPORT_ROUTES[type];
  return {
    id: report.id,
    type,
    typeLabel: NDT_REPORT_TYPE_LABELS[type],
    reportNumber: report.reportNumber || report.clientReportNumber || report.id,
    revision: report.revision || "—",
    date: (report.testDate || report.issueDate || "").slice(0, 10),
    // ME (ao contrário de LP/PM/USC) não tem projectId/unitAreaId no seu
    // domínio — só o texto de exibição. `?? null` normaliza o `undefined`
    // que vem de lá para o mesmo "sem vínculo" que os outros três já usam.
    projectId: report.projectId ?? null,
    projectLabel: report.projectContract || "—",
    unitAreaId: report.unitAreaId ?? null,
    unitAreaLabel: report.unitArea || "—",
    equipmentTag: report.equipmentTag || "—",
    responsible: report.inspectorName || "—",
    status: report.status,
    viewRoute: `${route}/${report.id}`,
    downloadRoute: `${route}/${report.id}`
  };
}

export class NdtReportCatalogService {
  constructor(private readonly deps: Deps) {}

  private can(context: RequestContext, permission: string): boolean {
    return !!(context.permissions && typeof (context.permissions as any).has === "function" && (context.permissions as any).has(permission));
  }

  /**
   * BUSCA — só devolve tipos que o contexto tem permissão de ver (item 35:
   * Inspetor/Técnico consultam só dentro do escopo autorizado; o filtro é
   * feito aqui, no serviço, nunca só escondido na tela).
   */
  async search(context: RequestContext, filter: NdtReportCatalogFilter = {}): Promise<NdtReportRow[]> {
    const clientId = context.activeClientId ?? undefined;
    const sources: Array<{ type: NdtReportType; load: () => Promise<readonly NdtReportCommon<NdtDocumentCommon>[]> }> = [
      { type: "ME", load: () => this.deps.meReportService.listByClient(context, clientId) as any },
      { type: "LP", load: () => this.deps.lpReportService.listByClient(context, clientId) as any },
      { type: "PM", load: () => this.deps.pmReportService.listByClient(context, clientId) as any },
      { type: "USC", load: () => this.deps.conventionalUsReportService.listByClient(context, clientId) as any },
      ...(this.deps.bscanReportService
        ? [{ type: "BSCAN" as const, load: () => this.deps.bscanReportService!.listByClient(context, clientId) as any }]
        : [])
    ];

    const rows: NdtReportRow[] = [];
    for (const source of sources) {
      if (filter.type && filter.type !== source.type) continue;
      if (!this.can(context, NDT_VIEW_PERMISSION[source.type])) continue;

      const reports = await source.load().catch(() => []);
      for (const report of reports) {
        const row = toRow(source.type, report);
        if (filter.projectId && row.projectId !== filter.projectId) continue;
        if (filter.unitAreaId && row.unitAreaId !== filter.unitAreaId) continue;
        if (filter.equipmentTag && !row.equipmentTag.toLowerCase().includes(filter.equipmentTag.toLowerCase())) continue;
        if (filter.status && row.status !== filter.status) continue;
        if (filter.from && row.date && row.date < filter.from) continue;
        if (filter.to && row.date && row.date > filter.to) continue;
        rows.push(row);
      }
    }

    return rows.sort((a, b) => b.date.localeCompare(a.date) || a.reportNumber.localeCompare(b.reportNumber, "pt-BR"));
  }
}
